import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (
      this.closest('product-card')?.querySelector('a[ref="productCardLink"]')
    )?.href;
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);

    /* ── Prefetch on hover/focus so the product page is cached
           before the user even clicks. On mobile, prefetch as
           soon as the component is in view (IntersectionObserver). ── */
    if ('IntersectionObserver' in window && isMobileBreakpoint()) {
      const io = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            this.#prefetch();
          }
        });
      }, { rootMargin: '200px' });
      io.observe(this);
    } else {
      this.addEventListener('mouseenter', this.#prefetch, { once: true, passive: true });
      this.addEventListener('focusin',    this.#prefetch, { once: true, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Silently prefetch the product page into cache.
   * Called on hover/focus (desktop) or IntersectionObserver (mobile).
   * Does nothing if already cached or no URL available.
   */
  #prefetch = () => {
    if (this.#cachedProductHtml) return;
    const url = this.productPageUrl;
    if (!url) return;
    this.fetchProductPage(url);
  };

  /**
   * Handles quick add button click
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );
      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page HTML
   * @param {string} productPageUrl
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('QuickAdd fetch error:', error);
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the quick add modal content
   * @param {Document} newHtml
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid) {
      console.warn('[QuickAdd] Could not find [data-product-grid-content] in fetched HTML.');
      return;
    }

    if (!modalContent) {
      console.warn('[QuickAdd] Could not find #quick-add-modal-content in DOM.');
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const productPrice = productGrid.querySelector('product-price');

      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || 'View Product';
      productTitle.href = this.productPageUrl;
      productTitle.className = 'product-title-link';

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      if (productPrice) productHeader.appendChild(productPrice);
      productHeader.appendChild(productTitle);

      if (!productFormComponent || !variantPicker) {
        console.warn('[QuickAdd] Missing variant picker or form component in mobile modal.');
        return;
      }

      // Append elements for mobile view
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);

      // Optionally remove productDetails to prevent duplication
      if (productDetails) {
        productDetails.remove();
      } else {
        console.warn('[QuickAdd] .product-details not found. Skipping removal.');
      }
    }

    // Finally morph the modal content
    morph(modalContent, productGrid);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CartUpdateEvent} event */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (event) => {
    const anchorElement = event.detail.data.html?.querySelector('.view-product-title a');
    const viewMoreDetailsLink = this.querySelector('.view-product-title a');
    const mobileProductTitle = this.querySelector('.product-header a');

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
